import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { ELEVES_ACCOUNT_KEY } from '@teetsh/app/src/queries/eleves/useEleves';
import { Eleve } from '@teetsh/app/src/types/eleves';
import { serializeEleve } from './serializer';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { useTranslation } from 'react-i18next';

export default function useCreateEleve() {
  const { schoolyear, school } = useSchoolSchoolyear();
  const { notifyError } = useNotification();
  const user = useUser();
  const { t } = useTranslation(['eleve']);

  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: ({
      eleves,
      schoolyearId,
      schoolId,
    }: {
      eleves: Eleve[];
      schoolyearId?: string;
      schoolId?: string;
    }) =>
      request(
        `/schoolyears/${schoolyearId || schoolyear.id}/schools/${
          schoolId || school.id
        }/eleves`,
        {
          method: 'POST',
          body: eleves
            .map(e => serializeEleve(e, user.location))
            .map(ev => ({
              id: ev.id,
              classroomId: ev.classroomId,
              givenName: ev.givenName,
              familyName: ev.familyName,
              groupName: ev.groupName,
              picture: ev.picture,
              ine: ev.ine,
              arrivalDate: ev.arrivalDate,
              radiationDate: ev.radiationDate,
              gender: ev.gender,
              birthday: ev.birthday,
              decloisonement: ev.decloisonement,
              customFields: ev.customFields,
              comment: ev.comment,
              groups: ev.groups,
            })),
        },
      ),
    onMutate: ({ eleves }) => {
      queryClient.setQueryData<Eleve[]>(
        [ELEVES_ACCOUNT_KEY, schoolyear.id, school.id],
        old => [...(old || []), ...eleves],
      );
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: [ELEVES_ACCOUNT_KEY],
      });
      notifyError({
        title: t('eleve:messages.createError'),
      });
    },
  });
  return mutateAsync;
}
